import { fetchExperienceList } from 'actions/ExperienceActions'
import { EmptyExperienceList } from 'constants/experience'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { take, without } from 'lib/array/arrayUtils'
import { getExperienceListKey } from 'lib/experiences/experienceUtils'
import { useEffect, useMemo } from 'react'

interface Params {
  latitude?: number
  longitude?: number
  categoryCodes?: Array<number>
  distance?: string
  from?: string
  to?: string
  postPurchaseOnly?: boolean
  offerId?: string
  limit?: number
  excludeIds?: Array<string>
  isLoyaltyEligible?: boolean
}

function useExperienceList(params: Params, requiresCoordinates = true): [App.ApiCallState<{ ids: Array<string> }>, Array<App.ExperienceOffer>] {
  const {
    distance,
    limit = 8,
    from,
    to,
    postPurchaseOnly,
    categoryCodes,
    latitude,
    longitude,
    offerId,
    excludeIds = [],
    isLoyaltyEligible,
  } = params

  const dispatch = useAppDispatch()

  const distanceRange = useMemo(() => distance ? ['0KM', distance] : undefined, [distance])

  const listKey = useMemo(() => {
    return getExperienceListKey({ latitude, longitude, distanceRange, categoryCodes, from, to, postPurchaseOnly, offerId, isLoyaltyEligible })
  }, [latitude, longitude, distanceRange, categoryCodes, from, to, postPurchaseOnly, offerId, isLoyaltyEligible])

  const experienceList = useAppSelector((state) => state.experience.experienceLists[listKey] ?? { ...EmptyExperienceList, fetching: false })
  const experiences = useAppSelector((state) => state.experience.experiences)

  useEffect(() => {
    if (!requiresCoordinates || (latitude && longitude)) {
      dispatch(fetchExperienceList({ latitude, longitude, distanceRange, categoryCodes, from, to, postPurchaseOnly, offerId, isLoyaltyEligible }))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryCodes, latitude, longitude, distanceRange, from, to, postPurchaseOnly, isLoyaltyEligible])

  const list = useMemo(() => {
    if (excludeIds.length === 0) {
      return experienceList
    }

    return {
      ...experienceList,
      ids: without(experienceList.ids, ...excludeIds),
    }
  }, [experienceList, excludeIds])

  const experienceItems = useMemo(() => {
    return take(list.ids, limit).map(id => experiences[id]).filter(Boolean)
  }, [experiences, list, limit])

  return [
    list,
    experienceItems,
  ]
}

export default useExperienceList
